<template>
  <EmsLayout>
    <div class="income-analysis-page">
      <!-- 顶部栏 -->
      <div class="analysis-header">
        <div class="analysis-title">峰谷收益分析</div>
        <div class="analysis-actions">
          <div class="time-toggle">
            <span
              v-for="t in timeTabs"
              :key="t.value"
              :class="['toggle-item', { active: activeTab === t.value }]"
              @click="handleTabChange(t.value)">
              {{ t.label }}
            </span>
          </div>
          <el-date-picker
            v-model="queryDate"
            size="mini"
            :type="datePickerType"
            :value-format="dateValueFormat"
            :format="dateDisplayFormat"
            :clearable="false"
            placeholder="选择时间"
            popper-class="income-date-picker"></el-date-picker>
          <el-button size="mini" type="primary" @click="handleQuery">
            <i class="el-icon-search"></i>
            查询
          </el-button>
          <el-button size="mini" @click="handleExport">
            <i class="el-icon-download"></i>
            导出
          </el-button>
        </div>
      </div>

      <!-- 统计概览 -->
      <div class="stats-overview">
        <div class="stat-item">
          <span class="stat-label">峰谷收益(¥)</span>
          <span class="stat-value">{{ summary.profit }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">充电金额(¥)</span>
          <span class="stat-value">{{ summary.chargeAmount }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">放电金额(¥)</span>
          <span class="stat-value">{{ summary.dischargeAmount }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">充电电量(kWh) {{ summary.chargeTotal }}</span>
          <div class="sub-stats">
            <span>
              尖
              <span class="sub-value">{{ summary.charge.jian }}</span>
            </span>
            <span>
              峰
              <span class="sub-value">{{ summary.charge.feng }}</span>
            </span>
            <span>
              平
              <span class="sub-value">{{ summary.charge.ping }}</span>
            </span>
            <span>
              谷
              <span class="sub-value">{{ summary.charge.gu }}</span>
            </span>
            <span>
              深
              <span class="sub-value">{{ summary.charge.shen }}</span>
            </span>
          </div>
        </div>
        <div class="stat-item">
          <span class="stat-label">放电电量(kWh) {{ summary.dischargeTotal }}</span>
          <div class="sub-stats">
            <span>
              尖
              <span class="sub-value">{{ summary.discharge.jian }}</span>
            </span>
            <span>
              峰
              <span class="sub-value">{{ summary.discharge.feng }}</span>
            </span>
            <span>
              平
              <span class="sub-value">{{ summary.discharge.ping }}</span>
            </span>
            <span>
              谷
              <span class="sub-value">{{ summary.discharge.gu }}</span>
            </span>
            <span>
              深
              <span class="sub-value">{{ summary.discharge.shen }}</span>
            </span>
          </div>
        </div>
      </div>

      <!-- 数据表格 -->
      <div class="table-wrapper" v-loading="loading">
        <table class="data-table">
          <thead>
            <tr class="header-row">
              <th rowspan="2" class="time-col">时间</th>
              <th colspan="8" class="group-col">充电电量(kWh)</th>
              <th colspan="8" class="group-col">放电电量(kWh)</th>
              <th colspan="3" class="group-col">金额(¥)</th>
            </tr>
            <tr class="sub-header-row">
              <th>期初</th>
              <th>期末</th>
              <th>本期</th>
              <th>尖</th>
              <th>峰</th>
              <th>平</th>
              <th>谷</th>
              <th>深</th>
              <th>期初</th>
              <th>期末</th>
              <th>本期</th>
              <th>尖</th>
              <th>峰</th>
              <th>平</th>
              <th>谷</th>
              <th>深</th>
              <th>充电</th>
              <th>放电</th>
              <th>收益</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in paginatedData" :key="index">
              <td class="time-col">{{ row.time }}</td>
              <td>{{ row.charge.begin }}</td>
              <td>{{ row.charge.end }}</td>
              <td>{{ row.charge.current }}</td>
              <td>{{ row.charge.jian }}</td>
              <td>{{ row.charge.feng }}</td>
              <td>{{ row.charge.ping }}</td>
              <td>{{ row.charge.gu }}</td>
              <td>{{ row.charge.shen }}</td>
              <td>{{ row.discharge.begin }}</td>
              <td>{{ row.discharge.end }}</td>
              <td>{{ row.discharge.current }}</td>
              <td>{{ row.discharge.jian }}</td>
              <td>{{ row.discharge.feng }}</td>
              <td>{{ row.discharge.ping }}</td>
              <td>{{ row.discharge.gu }}</td>
              <td>{{ row.discharge.shen }}</td>
              <td>{{ row.amount.charge }}</td>
              <td>{{ row.amount.discharge }}</td>
              <td :class="row.amount.profitClass">{{ row.amount.profit }}</td>
            </tr>
            <tr v-if="!loading && paginatedData.length === 0">
              <td colspan="20" class="empty-row">暂无数据</td>
            </tr>
          </tbody>
        </table>
        <!-- 分页 -->
        <div class="pagination-wrapper" v-if="tableData.length > pageSize">
          <el-pagination
            :current-page="currentPage"
            :page-size="pageSize"
            :total="tableData.length"
            layout="prev, pager, next"
            @current-change="handlePageChange"></el-pagination>
        </div>
      </div>
    </div>
  </EmsLayout>
</template>

<script>
  import dayjs from 'dayjs';
  import EmsLayout from '../layout/EmsLayout.vue';
  import { queryEmsHistory } from '@/service/emsApis';
  import { fmtCompact, toNum } from '../utils';

  export default {
    name: 'IncomeAnalysis',
    components: {
      EmsLayout,
    },
    data() {
      return {
        loading: false,
        activeTab: 'day',
        timeTabs: [
          { label: '日', value: 'day' },
          { label: '月', value: 'month' },
          { label: '年', value: 'year' },
        ],
        queryDate: dayjs().format('YYYY-MM-DD'),
        tableData: [],
        summary: this.buildEmptySummary(),
        // 分页
        currentPage: 1,
        pageSize: 8,
      };
    },
    computed: {
      stationCode() {
        return this.$store.getters.emsStationCode;
      },
      paginatedData() {
        const start = (this.currentPage - 1) * this.pageSize;
        const end = start + this.pageSize;
        return this.tableData.slice(start, end);
      },
      datePickerType() {
        if (this.activeTab === 'month') return 'month';
        if (this.activeTab === 'year') return 'year';
        return 'date';
      },
      dateValueFormat() {
        if (this.activeTab === 'month') return 'yyyy-MM';
        if (this.activeTab === 'year') return 'yyyy';
        return 'yyyy-MM-dd';
      },
      dateDisplayFormat() {
        if (this.activeTab === 'month') return 'yyyy-MM';
        if (this.activeTab === 'year') return 'yyyy';
        return 'yyyy-MM-dd';
      },
    },
    watch: {
      stationCode() {
        this.currentPage = 1;
        this.fetchData();
      },
    },
    mounted() {
      this.fetchData();
    },
    methods: {
      buildEmptySummary() {
        return {
          profit: '0',
          dischargeAmount: '0',
          chargeAmount: '0',
          dischargeTotal: 0,
          chargeTotal: 0,
          discharge: { jian: 0, feng: 0, ping: 0, gu: 0, shen: 0 },
          charge: { jian: 0, feng: 0, ping: 0, gu: 0, shen: 0 },
        };
      },

      handleTabChange(value) {
        if (this.activeTab === value) return;
        this.activeTab = value;
        // 切换维度后重置查询时间为当前的对应单位
        if (value === 'day') {
          this.queryDate = dayjs().format('YYYY-MM-DD');
        } else if (value === 'month') {
          this.queryDate = dayjs().format('YYYY-MM');
        } else {
          this.queryDate = dayjs().format('YYYY');
        }
        this.fetchData();
      },

      handleQuery() {
        this.fetchData();
      },

      handlePageChange(page) {
        this.currentPage = page;
      },

      buildQueryParams() {
        let proc;
        let start;
        let end;
        if (this.activeTab === 'day') {
          proc = 'profit_1h';
          const d = dayjs(this.queryDate);
          start = d.startOf('day').format('YYYY-MM-DD HH:mm:ss');
          end = d.endOf('day').format('YYYY-MM-DD HH:mm:ss');
        } else if (this.activeTab === 'month') {
          proc = 'profit_1d';
          const d = dayjs(this.queryDate);
          start = d.startOf('month').format('YYYY-MM-DD HH:mm:ss');
          end = d.endOf('month').format('YYYY-MM-DD HH:mm:ss');
        } else {
          proc = 'profit_mn';
          const d = dayjs(this.queryDate);
          start = d.startOf('year').format('YYYY-MM-DD HH:mm:ss');
          end = d.endOf('year').format('YYYY-MM-DD HH:mm:ss');
        }
        return {
          proc,
          param: [this.stationCode, start, end],
        };
      },

      async fetchData() {
        if (!this.stationCode) return;
        this.loading = true;
        try {
          const params = this.buildQueryParams();
          const res = await queryEmsHistory(params);
          const list = Array.isArray(res) ? res : res?.data || [];
          this.tableData = list.map(item => this.toRow(item));
          this.summary = this.calcSummary(list);
        } catch (e) {
          console.error('获取峰谷收益数据失败', e);
          this.tableData = [];
          this.summary = this.buildEmptySummary();
        } finally {
          this.loading = false;
        }
      },

      toRow(item) {
        const profit = this.toNum(item.mny);
        return {
          time: this.formatRowTime(item.attm),
          charge: {
            begin: this.fmt(item.q_i_b),
            end: this.fmt(item.q_i_e),
            current: this.fmt(item.q_i),
            jian: this.fmt(item.q1_i),
            feng: this.fmt(item.q2_i),
            ping: this.fmt(item.q3_i),
            gu: this.fmt(item.q4_i),
            shen: this.fmt(item.q5_i),
          },
          discharge: {
            begin: this.fmt(item.q_o_b),
            end: this.fmt(item.q_o_e),
            current: this.fmt(item.q_o),
            jian: this.fmt(item.q1_o),
            feng: this.fmt(item.q2_o),
            ping: this.fmt(item.q3_o),
            gu: this.fmt(item.q4_o),
            shen: this.fmt(item.q5_o),
          },
          amount: {
            discharge: this.fmt(item.mny_o),
            charge: this.fmt(item.mny_i),
            profit: this.fmt(item.mny),
            // eslint-disable-next-line no-nested-ternary
            profitClass: profit > 0 ? 'profit' : profit < 0 ? 'loss' : '',
          },
        };
      },

      formatRowTime(attm) {
        if (!attm) return '--';
        if (this.activeTab === 'day') {
          // profit_1h 返回完整时间 "2026-05-19 00:04:22"
          return dayjs(attm).format('YYYY-MM-DD HH:mm:ss');
        }
        if (this.activeTab === 'month') {
          // profit_1d 返回日期 "2026-05-01"
          return dayjs(attm).format('YYYY-MM-DD');
        }
        // profit_mn 返回月度起始日期 "2026-04-24"，按月展示
        return dayjs(attm).format('YYYY-MM');
      },

      calcSummary(list) {
        const sum = {
          mny: 0,
          mny_i: 0,
          mny_o: 0,
          q_i: 0,
          q_o: 0,
          q1_i: 0,
          q2_i: 0,
          q3_i: 0,
          q4_i: 0,
          q5_i: 0,
          q1_o: 0,
          q2_o: 0,
          q3_o: 0,
          q4_o: 0,
          q5_o: 0,
        };
        list.forEach(item => {
          Object.keys(sum).forEach(k => {
            sum[k] += this.toNum(item[k]);
          });
        });
        // 月维度后端可能未返回 q_i / q_o（仅有期初期末），用尖+峰+平+谷+深之和兜底
        if (this.activeTab === 'year') {
          if (sum.q_i === 0) {
            sum.q_i = sum.q1_i + sum.q2_i + sum.q3_i + sum.q4_i + sum.q5_i;
          }
          if (sum.q_o === 0) {
            sum.q_o = sum.q1_o + sum.q2_o + sum.q3_o + sum.q4_o + sum.q5_o;
          }
        }
        const round = v => this.fmt(v);
        return {
          profit: round(sum.mny),
          dischargeAmount: round(sum.mny_o),
          chargeAmount: round(sum.mny_i),
          dischargeTotal: round(sum.q_o),
          chargeTotal: round(sum.q_i),
          discharge: {
            jian: round(sum.q1_o),
            feng: round(sum.q2_o),
            ping: round(sum.q3_o),
            gu: round(sum.q4_o),
            shen: round(sum.q5_o),
          },
          charge: {
            jian: round(sum.q1_i),
            feng: round(sum.q2_i),
            ping: round(sum.q3_i),
            gu: round(sum.q4_i),
            shen: round(sum.q5_i),
          },
        };
      },

      toNum,

      fmt: fmtCompact,

      handleExport() {
        if (!this.tableData.length) {
          this.$message?.warning?.('暂无数据可导出');
          return;
        }
        const headers = [
          '时间',
          '充电电量-期初',
          '充电电量-期末',
          '充电电量-本期',
          '充电电量-尖',
          '充电电量-峰',
          '充电电量-平',
          '充电电量-谷',
          '充电电量-深',
          '放电电量-期初',
          '放电电量-期末',
          '放电电量-本期',
          '放电电量-尖',
          '放电电量-峰',
          '放电电量-平',
          '放电电量-谷',
          '放电电量-深',
          '金额-充电',
          '金额-放电',
          '金额-收益',
        ];
        const rows = this.tableData.map(r => [
          r.time,
          r.charge.begin,
          r.charge.end,
          r.charge.current,
          r.charge.jian,
          r.charge.feng,
          r.charge.ping,
          r.charge.gu,
          r.charge.shen,
          r.discharge.begin,
          r.discharge.end,
          r.discharge.current,
          r.discharge.jian,
          r.discharge.feng,
          r.discharge.ping,
          r.discharge.gu,
          r.discharge.shen,
          r.amount.charge,
          r.amount.discharge,
          r.amount.profit,
        ]);
        const escape = v => {
          const s = String(v ?? '');
          if (s.includes(',') || s.includes('"') || s.includes('\n')) {
            return `"${s.replace(/"/g, '""')}"`;
          }
          return s;
        };
        const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\n');
        const bom = '\uFEFF';
        const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const dimLabelMap = { day: '日', month: '月', year: '年' };
        const dimLabel = dimLabelMap[this.activeTab];
        a.download = `峰谷收益分析_${this.stationCode}_${dimLabel}_${this.queryDate}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '../styles/variables.scss';

  .income-analysis-page {
    width: 100%;
    height: 100%;
    background: $bg-primary;
    padding: 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  // 顶部栏
  .analysis-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: $bg-card;
    border: 1px solid $bg-card-border;
    border-radius: 8px;
  }

  .analysis-title {
    font-size: 14px;
    font-weight: 600;
    color: $accent-blue;
  }

  .analysis-actions {
    display: flex;
    align-items: center;
    gap: 12px;

    .time-toggle {
      display: flex;
      background: #f6f7f8;
      border: 1px solid $bg-card-border;
      border-radius: 4px;
      overflow: hidden;

      .toggle-item {
        padding: 4px 12px;
        font-size: 12px;
        color: $text-secondary;
        cursor: pointer;
        transition: all 0.2s;

        &.active {
          background: $accent-blue;
          color: #fff;
        }

        &:hover:not(.active) {
          color: $accent-blue;
        }
      }
    }
  }

  // 统计概览
  .stats-overview {
    display: flex;
    gap: 12px;
    padding: 12px 16px;
    background: $bg-card;
    border: 1px solid $bg-card-border;
    border-radius: 8px;
    overflow-x: auto;

    .stat-item {
      flex: 1;
      min-width: 160px;
      display: flex;
      flex-direction: column;
      gap: 8px;

      .stat-label {
        font-size: 14px;
        color: $text-primary;
        font-weight: 500;
      }

      .stat-value {
        font-size: 24px;
        font-weight: bold;
        color: $accent-yellow;
        font-family: 'DIN Alternate', 'Helvetica Neue', Arial, sans-serif;
      }

      .sub-stats {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;

        span {
          font-size: 13px;
          color: $text-secondary;
        }

        .sub-value {
          color: $accent-yellow;
          font-weight: bold;
          margin-left: 2px;
          font-size: 13px;
          font-family: 'DIN Alternate', 'Helvetica Neue', Arial, sans-serif;
        }
      }
    }
  }

  // 表格
  .table-wrapper {
    flex: 1;
    overflow: auto;
    background: $bg-card;
    border: 1px solid $bg-card-border;
    border-radius: 8px;
  }

  // 分页
  .pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    padding: 12px 16px;
    background: $bg-card;
    border-top: 1px solid $bg-card-border;

    ::v-deep .el-pagination {
      .btn-prev,
      .btn-next {
        background: transparent;
        border: 1px solid $bg-card-border;
        color: $text-secondary;
        &:hover {
          color: $accent-blue;
        }
      }
      .el-pager li {
        background: transparent;
        border: 1px solid $bg-card-border;
        color: $text-secondary;
        &.active {
          background: $accent-blue;
          border-color: $accent-blue;
          color: #fff;
        }
        &:hover {
          color: $accent-blue;
        }
      }
    }
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    color: $text-primary;

    th,
    td {
      padding: 10px 8px;
      text-align: center;
      border: 1px solid #ebebf0;
      white-space: nowrap;
    }

    th {
      background: rgba(0, 212, 255, 0.05);
      color: $text-secondary;
      font-weight: 500;
    }

    .header-row th {
      border-bottom: 1px solid rgba(0, 212, 255, 0.2);
    }

    .group-col {
      background: rgba(0, 212, 255, 0.03);
    }

    tbody tr {
      &:nth-child(even) {
        background: #f6f7f8;
      }

      &:hover {
        background: rgba(0, 212, 255, 0.03);
      }
    }

    .time-col {
      text-align: left;
      padding-left: 12px;
    }

    .empty-row {
      text-align: center;
      color: $text-secondary;
      padding: 32px 0;
    }

    .profit {
      color: $accent-green;
    }

    .loss {
      color: $accent-red;
    }
  }
</style>

<style lang="scss">
  .income-date-picker {
    background: #fff !important;
    border: 1px solid #ebebf0 !important;

    .el-date-picker__header,
    .el-picker-panel__content {
      color: #606266;
    }

    .el-date-table th {
      color: #909399;
    }

    .el-date-table td {
      color: #303133;
    }

    .el-date-table td.next-month,
    .el-date-table td.prev-month {
      color: #c0c4cc;
    }

    .el-date-table td.current:not(.disabled) span {
      background: #4d8cfd !important;
      color: #fff !important;
    }

    .el-date-table td.today span {
      color: #4d8cfd;
    }

    .el-picker-panel__icon-btn {
      color: #909399;
    }

    .el-date-picker__header-label {
      color: #303133;
    }
  }
</style>
